import React, { useState, useEffect } from 'react';
import { Layout, ConfigProvider, Spin } from 'antd'; 
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useAuth, AuthProvider } from './context/AuthContext';
import PrivateRoute from './context/PrivateRoute'; 
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ScreenSizeProvider, useScreenSize } from './context/ScreenSizeContext'; 
import getThemeConfig from './themeConfig';
import useWebSocket from './hooks/useWebSocket';
import { WS_URL } from './config';
import './styles/App.css';
import 'leaflet/dist/leaflet.css';

//Routes
import LoginPage from './components/LoginPage';
import AppHeader from './components/Pages/AppHeader';
import Sidebar from './components/Pages/Sidebar';
import ProfilePage from './components/Users/ProfilePage';
import MainGuide from './components/DsyncGuide/MainGuide';
import Home from './components/Pages/Home';
//Admin
import AdminPanel from './components/Administration/AdminPanel';
import AdminEvents from './components/Administration/EventListAdmin';
import AdminDevices from './components/Administration/DeviceListAdmin';
import AdminDeviceConfig from './components/Administration/DeviceConfigListAdmin';
import LeadPolicyEditorListAdmin from './components/Administration/LeadPolicyEditorListAdmin';
import ConsoleLogs from './components/Administration/ConsoleLogs';
import ClientReport from './components/Administration/ClientReport';
import EventsPlannerAdmin from './components/Administration/Planner/EventsPlannerAdmin';
//Users
import DeviceList from './components/Devices/DeviceList';
import DeviceConfigList from './components/DeviceConfigs/DeviceConfigList';
import SlackNotifications from './components/SlackNotifications';
import EventList from './components/Events/EventList';
import ParticipantsList from './components/Participants/ParticipantsList';
import EventDetails from './components/Events/EventDetails';
import LeadPolicyEditorList from './components/LeadPolicy/LeadPolicyEditorList';
import AppsList from './components/Apps/AppsList';
import DesignSettings from './components/Design/DesignSettings';
import ControlList from './components/ControlList/ControlList';
import {MdSyncProblem} from "react-icons/md";
import DsyncGuide from "./components/DsyncGuide/MainGuide";

const { Content: AntContent, Header } = Layout;

const AppContent = () => {
  const { user, isLoading } = useAuth();
  const { darkTheme, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false); 
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { isMobile, isTablet } = useScreenSize(); 

  useEffect(() => {
    setCollapsed(isTablet); // If it is a tablet, collapse the Sidebar, otherwise expand it.
  }, [isTablet]);

  const handleToggleTheme = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      toggleTheme(); // Change the theme after the middle of the transition.
      setTimeout(() => {
        setIsTransitioning(false); // Completion of the transition
      }, 800); // Total animation duration
    }, 300); // Moment of change of subject
  };

  const handleCollapseToggle = () => {
    setCollapsed(!collapsed); // Alternate collapsed state
  };

  if (isLoading) {
    return (
      <Spin size="large" tip="Loading..." style={{ height: '100vh' }}>
        <div style={{ height: '100vh', backgroundColor: 'rgba(0,0,0,0.05)' }} />
      </Spin>
    );
  }

  return (
  <div className={`app ${darkTheme ? 'dark' : 'light'}`}>
      {/* Transition overlay */}
      {isTransitioning && (
        <div
          className={`theme-transition-overlay ${darkTheme ? 'dark' : 'light'}`}
        />
      )}
    {/* Main container to unify the transition */}
    <div
      style={{
        transition: 'background-color 0.8s ease, color 0.8s ease',
        backgroundColor: darkTheme ? '#1f1f1f' : '#ffffff',
        minHeight: '100vh',
        display: 'flex', 
        flexDirection: 'column',
      }}
    >
    <Routes>
        {/* Public route*/}
      <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} /> 
      
      {/* Protected routes */}
      <Route
        path="*"
        element={
          <Layout style={{
            transition: 'background-color 0.8s ease, color 0.8s ease', 
            backgroundColor: darkTheme ? '#141414' : '#ffffff',
            minHeight: '100vh',
            display: 'flex',
          }} >
            {!isMobile && ( // Hide Sidebar on mobile devices
              <Sidebar 
                collapsed={collapsed} 
                toggleCollapse={handleCollapseToggle} 
                toggleTheme={handleToggleTheme} 
                darkTheme={darkTheme} 
              />
            )}
            <Layout
              style={{
                transition: 'margin-left 0.3s',
                marginLeft: collapsed ? '0px' : '0px',
                flexGrow: 1,
                overflowY: 'auto', // Scroll ONLY in the content
                height: '100vh',
              }}
            >
              <Header style={{
                backgroundColor: darkTheme ? '#141414' : '#ffffff',
                padding: '0 24px',
                zIndex: 1,
                transition: 'background-color 0.8s ease',
              }}>
                <AppHeader />
              </Header>
              <AntContent
                style={{
                  backgroundColor: darkTheme ? '#141414' : '#ffffff',
                  flex: 1,
                  overflowY: 'auto', // Scroll ONLY in the content
                  transition: 'background-color 0.8s ease, color 0.8s ease',
                }}
              >
                <Routes>
                  <Route path="/" element={ <PrivateRoute><Home /></PrivateRoute>} />
                  <Route path="/devices" element={ <PrivateRoute><DeviceList darkTheme={darkTheme} toggleTheme={toggleTheme} /></PrivateRoute>} />
                  <Route path="/deviceconfigs" element={<PrivateRoute><DeviceConfigList /></PrivateRoute>} />
                  <Route path="/slack-notifications/:id" element={<PrivateRoute><SlackNotifications /></PrivateRoute>} />
                  <Route path="/events" element={<PrivateRoute><EventList /></PrivateRoute>} />
                  <Route path="/event-details" element={<PrivateRoute><EventDetails /></PrivateRoute>} />
                  <Route path="/participants" element={
                    <PrivateRoute requiredFeature="participants.view"><ParticipantsList /></PrivateRoute>
                  } />
                  <Route path="/lead-policy" element={
                    <PrivateRoute requiredFeature="leadpolicy.view"><LeadPolicyEditorList /></PrivateRoute>
                  } />
                  <Route path="/lead-control" element={
                    <PrivateRoute requiredFeature="lead-control.view"><ControlList darkTheme={darkTheme} toggleTheme={toggleTheme} /></PrivateRoute>
                  } />
                  <Route path="/apps" element={<PrivateRoute><AppsList darkTheme={darkTheme} toggleTheme={toggleTheme} /></PrivateRoute>} />
                  <Route path="/design" element={
                    <PrivateRoute requiredFeature="deviceConfig.design"><DesignSettings /></PrivateRoute>
                  } />
                  <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
                  <Route path="/guide" element={<PrivateRoute><MainGuide /></PrivateRoute>} />
                  {user && user.groupID?.name === 'admin' ? (
                    <>
                    <Route path="/admin-panel" element={<PrivateRoute><AdminPanel /></PrivateRoute>} />
                    <Route path="/admin-events" element={<PrivateRoute><AdminEvents darkTheme={darkTheme} toggleTheme={toggleTheme} /></PrivateRoute>} />
                    <Route path="/admin-devices" element={<PrivateRoute><AdminDevices darkTheme={darkTheme} toggleTheme={toggleTheme} /></PrivateRoute>} />
                    <Route path="/admin-deviceconfigs" element={<PrivateRoute><AdminDeviceConfig darkTheme={darkTheme} toggleTheme={toggleTheme} /></PrivateRoute>} />
                    <Route path="/admin-lead-policy" element={<PrivateRoute><LeadPolicyEditorListAdmin darkTheme={darkTheme} toggleTheme={toggleTheme} /></PrivateRoute>} />
                    <Route path="/admin-console-logs" element={<PrivateRoute><ConsoleLogs darkTheme={darkTheme} toggleTheme={toggleTheme} /></PrivateRoute>} />
                    <Route path="/client-report" element={<PrivateRoute><ClientReport darkTheme={darkTheme} toggleTheme={toggleTheme} /></PrivateRoute>} />
                    <Route path="/admin-planner" element={<PrivateRoute><EventsPlannerAdmin darkTheme={darkTheme} toggleTheme={toggleTheme} /></PrivateRoute>} />
                    </>
                  ) : (
                    <>
                    <Route path="/admin-panel" element={<Navigate to="/" />} />
                    <Route path="/admin-events" element={<Navigate to="/" />} />
                    <Route path="/admin-devices" element={<Navigate to="/" />} />
                    <Route path="/admin-deviceconfigs" element={<Navigate to="/" />} />
                    <Route path="/admin-lead-policy" element={<Navigate to="/" />} />
                    <Route path="/admin-console-logs" element={<Navigate to="/" />} />
                    <Route path="/client-report" element={<Navigate to="/" />} />
                    </>
                  )}
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </AntContent>
            </Layout>
          </Layout>
        }
      />
    </Routes>
    </div>
  </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <ScreenSizeProvider>
          <Router>
            <WebSocketWrapper>
              <ConfigWrapper />
            </WebSocketWrapper>
          </Router>
        </ScreenSizeProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

const WebSocketWrapper = ({ children }) => {
  const { user } = useAuth();
  const { reconnect, checkConnection } = useWebSocket(
    WS_URL,
    user?._id,
    (data) => console.log('Message received from WebSocket:', data)
  );

  // Verify connection to WebSocket periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (user?._id && !checkConnection()) {
        //console.log('WebSocket disconnected, trying to reconnect...');
        reconnect();
      }
    }, 30000); //Checks every 30 seconds

    return () => clearInterval(interval); // Cleans the interval when disassembling
  }, [user, checkConnection, reconnect]);

  return <>{children}</>;
};

const ConfigWrapper = () => {
  const { darkTheme } = useTheme();
  return (
    <ConfigProvider theme={getThemeConfig(darkTheme)}>
      <AppContent />
    </ConfigProvider>
  );
};

export default App;
